import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { LuChevronDown, LuChevronUp } from 'react-icons/lu';
import { prepareRichHtml } from 'helpers/richHtml';
import LazyImageLightbox from './LazyImageLightbox';

// Ảnh trong nội dung hiện như THUMBNAIL, bấm vào mới xem cỡ lớn:
//   - max-h theo màn hình (280px, 200px trên mobile), w-auto + object-contain:
//     giữ đúng tỉ lệ, không crop — bản vẽ không được mất góc nào.
//   - `!` (important): TinyMCE có thể ghi width/height inline khi người dùng kéo
//     resize trong editor; không ép thì một ảnh 1400px vẫn bung hết khung.
//   - inline-block: nhiều ảnh trong cùng đoạn xếp hàng ngang thay vì chồng dọc.
const IMAGE_CLS = [
  '[&_img]:!tw-max-h-[280px] max-sm:[&_img]:!tw-max-h-[200px]',
  '[&_img]:!tw-w-auto [&_img]:!tw-h-auto [&_img]:!tw-max-w-full [&_img]:tw-object-contain',
  '[&_img]:tw-inline-block [&_img]:tw-align-top [&_img]:tw-mr-2 [&_img]:tw-my-1.5',
  '[&_img]:tw-rounded-md [&_img]:tw-ring-1 [&_img]:tw-ring-slate-200 [&_img]:tw-bg-slate-50',
  '[&_img]:tw-cursor-zoom-in [&_img]:tw-transition-shadow hover:[&_img]:tw-shadow-md',
].join(' ');

// Chiều cao nội dung vượt ngưỡng thu gọn một khoảng nhỏ thì hiện luôn cho xong —
// bấm "Show more" chỉ để lộ thêm vài pixel là thao tác thừa.
const COLLAPSE_SLACK_PX = 48;

const collectSlides = (root, clicked) => {
  const images = Array.from(root.querySelectorAll('img')).filter((img) => img.getAttribute('src'));
  return {
    slides: images.map((img) => ({ src: img.currentSrc || img.src, alt: img.alt || '' })),
    index: Math.max(images.indexOf(clicked), 0),
  };
};

/**
 * Hiện HTML rich-text (TinyMCE) đã sanitize: ảnh dạng thumbnail mở lightbox có
 * zoom, nội dung dài thu gọn kèm "Show more".
 */
const RichContent = ({ html, className, style, collapsible, collapsedHeight }) => {
  const prepared = useMemo(() => prepareRichHtml(html), [html]);
  const innerRef = useRef(null);
  const [gallery, setGallery] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [overflowing, setOverflowing] = useState(false);

  // Đo trên khối bên trong (không bị giới hạn chiều cao): ảnh lazy tải xong làm
  // nội dung cao thêm, ResizeObserver bắt được còn đo một lần lúc mount thì không.
  useEffect(() => {
    const inner = innerRef.current;
    if (!collapsible || !inner) return undefined;

    const measure = () => setOverflowing(inner.scrollHeight > collapsedHeight + COLLAPSE_SLACK_PX);
    measure();

    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(inner);
    return () => observer.disconnect();
  }, [collapsible, collapsedHeight, prepared]);

  const handleClick = useCallback((event) => {
    const inner = innerRef.current;
    const img = event.target?.closest?.('img');
    if (!inner || !img || !inner.contains(img)) return;

    // Ảnh bọc trong <a> (TinyMCE "link image") — mở lightbox thay vì điều hướng.
    event.preventDefault();
    setGallery(collectSlides(inner, img));
  }, []);

  const closeGallery = useCallback(() => setGallery(null), []);
  const toggleExpanded = useCallback(() => setExpanded((prev) => !prev), []);

  if (!prepared) return null;

  const clamped = collapsible && overflowing && !expanded;

  return (
    <>
      <div
        className="tw-relative"
        style={clamped ? { maxHeight: collapsedHeight, overflow: 'hidden' } : undefined}
      >
        <div
          ref={innerRef}
          className={`${IMAGE_CLS} ${className}`}
          style={{ wordBreak: 'break-word', overflowWrap: 'break-word', ...style }}
          onClick={handleClick}
          dangerouslySetInnerHTML={{ __html: prepared }}
        />
        {clamped && (
          <div
            aria-hidden
            className="tw-pointer-events-none tw-absolute tw-inset-x-0 tw-bottom-0 tw-h-16 tw-bg-gradient-to-t tw-from-white tw-to-transparent"
          />
        )}
      </div>

      {collapsible && overflowing && (
        <button
          type="button"
          onClick={toggleExpanded}
          aria-expanded={expanded}
          className="tw-mt-1 tw-inline-flex tw-items-center tw-gap-1 tw-p-0 tw-border-0 tw-bg-transparent tw-text-xs tw-font-semibold tw-text-primary-600 hover:tw-text-primary-800 tw-cursor-pointer"
        >
          {expanded ? <LuChevronUp size={12} /> : <LuChevronDown size={12} />}
          {expanded ? 'Show less' : 'Show more'}
        </button>
      )}

      <LazyImageLightbox gallery={gallery} onClose={closeGallery} />
    </>
  );
};

RichContent.propTypes = {
  html: PropTypes.string,
  /** Typography của từng nơi dùng (description, response…) */
  className: PropTypes.string,
  style: PropTypes.object,
  /** Thu gọn nội dung dài, kèm nút "Show more" */
  collapsible: PropTypes.bool,
  collapsedHeight: PropTypes.number,
};

RichContent.defaultProps = {
  html: '',
  className: '',
  style: undefined,
  collapsible: false,
  collapsedHeight: 360,
};

export default RichContent;
